import { InputForm } from "../types";

const ASSET_TAG_COLUMN = {
    field: 'assetTag',
    headerName: 'Asset Tag',
    width: 200,
};

// Assigned tab only — unassigned devices have no user to read it from.
// Sort field is the post-$lookup path so server-side sorting works.
export const EMPLOYEE_ID_COLUMN = {
    field: 'user.employeeId',
    headerName: 'Employee ID',
    width: 150,
};

// Assigned tab only — the holder's live group/team, resolved client-side
// (user.teamId → Team.name, Team.groupId → Group.name) from the same
// team/group lists the filter bar loads. The names live outside the
// Devices query, so these columns cannot be server-sorted.
export const GROUP_COLUMN = {
    field: 'groupName',
    headerName: 'Group',
    width: 150,
    sortable: false,
};

export const TEAM_COLUMN = {
    field: 'teamName',
    headerName: 'Team',
    width: 150,
    sortable: false,
};

// Shipping-manifest snapshot fields (unassigned tab, asset management
// only) — free-text labels captured at bulk import: who the device was
// shipped to and their group/team at ship time. Not live org references.
// Single source of truth for the table columns, the filter inputs below,
// and the filter panel in devices-filters.tsx.
export const SHIPPED_FIELDS = [
    { field: 'shippedTo', headerName: 'Shipped To', placeholder: 'Search by shipped-to name...', width: 150 },
    { field: 'shippedToEmployeeId', headerName: 'Shipped To Employee ID', placeholder: 'Search by employee ID...', width: 180 },
    { field: 'shippedToGroup', headerName: 'Shipped To Group', placeholder: 'Search by group...', width: 150 },
    { field: 'shippedToTeam', headerName: 'Shipped To Team', placeholder: 'Search by team...', width: 150 },
] as const;

export const SHIPPED_COLUMNS = SHIPPED_FIELDS.map(({ field, headerName, width }) => ({ field, headerName, width }));

// Condensed on-screen presentation: the Unassigned table leads with the
// four shipped fields as two combined columns (name over employee ID;
// group · team), mirroring how the Assigned tab leads with the user.
// Filters and CSV exports keep all four fields separate. Sort fields are
// the primary value of each pair.
export const SHIPPED_TABLE_COLUMNS = [
    { field: 'shippedTo', headerName: 'Shipped To', width: 180 },
    { field: 'shippedToGroup', headerName: 'Group / Team', width: 200 },
];

export const DEVICE_COLUMNS = [
    {
        field: 'name',
        headerName: 'User Name',
        width: 150,
    },
    {
        field: 'serialNumber',
        headerName: 'Serial Number',
        width: 250,
    },
    {
        field: 'macId',
        headerName: 'Mac Id',
        width: 250,
    },
    {
        field: 'batteryLevel',
        headerName: 'Battery Level',
        width: 250,
    },
    {
        field: 'createdAt',
        headerName: 'Created At',
        width: 250,
    },
    {
        field: 'updatedAt',
        headerName: 'Last Update',
        width: 250,
    },
];

export const DEVICE_COLUMNS_WITH_ASSET_TAG = [
    DEVICE_COLUMNS[0], // User Name
    DEVICE_COLUMNS[1], // Serial Number
    ASSET_TAG_COLUMN,
    ...DEVICE_COLUMNS.slice(2), // Mac Id, Battery, Created, Updated
];

export const DEVICE_FILTER_INPUTS: InputForm[] = [
    {
        label: 'User Name',
        id: 'name',
        type: 'text',
        required: false,
        placeholder: 'Filter by device user name',
        grid: {
            xs: 4,
            sm: 4,
            md: 4,
        },
    },
    {
        label: 'Serial Number',
        id: 'serialNumber',
        type: 'text',
        required: false,
        placeholder: 'Filter by serial number',
        grid: {
            xs: 4,
            sm: 4,
            md: 4,
        },
    },
];

export const DEVICE_FILTER_INPUTS_WITH_ASSET_TAG: InputForm[] = [
    ...DEVICE_FILTER_INPUTS.map(filter => ({
        ...filter,
        grid: { xs: 3, sm: 3, md: 3 },
    })),
    {
        label: 'Asset Tag',
        id: 'assetTag',
        type: 'text',
        required: false,
        placeholder: 'Filter by asset tag',
        grid: {
            xs: 3,
            sm: 3,
            md: 3,
        },
    },
];

export const DEVICE_FILTER_INPUTS_UNASSIGNED: InputForm[] = [
    // No User Name input — unassigned devices have no user, so the API
    // ignores a name filter. People are searched via Shipped To instead.
    ...DEVICE_FILTER_INPUTS.filter((f) => f.id !== 'name').map(filter => ({
        ...filter,
        grid: { xs: 3, sm: 3, md: 3 },
    })),
    {
        label: 'Asset Tag',
        id: 'assetTag',
        type: 'text',
        required: false,
        placeholder: 'Filter by asset tag',
        grid: {
            xs: 3,
            sm: 3,
            md: 3,
        },
    },
    {
        label: 'Return Status',
        id: 'returnStatus',
        type: 'select',
        required: false,
        placeholder: 'Filter by return status',
        options: [
            { label: 'All', value: '' },
            { label: 'Not Set', value: 'not_set' },
            { label: 'Not Returned', value: 'not_returned' },
            { label: 'Returned', value: 'returned' },
            { label: 'In Transit', value: 'in_transit' },
        ],
        grid: {
            xs: 3,
            sm: 3,
            md: 3,
        },
    },
    ...SHIPPED_FIELDS.map((f) => ({
        label: f.headerName,
        id: f.field,
        type: 'text' as const,
        required: false,
        placeholder: f.placeholder,
        grid: { xs: 3, sm: 3, md: 3 },
    })),
];
